import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { carsApi, bidApi, imageUrl, API_BASE } from "../../lib/api";
import axios from "axios";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { ArrowLeft, Upload, X, Save, Plus, GripVertical, Check, ChevronDown, Calculator, ScanSearch, Loader2 } from "lucide-react";
import { OPTION_CATEGORIES, ALL_OPTIONS, translateOption, translateCategory } from "../../lib/carOptions";
import { useI18n } from "../../i18n/I18nContext";
import { computeBid } from "./AdminBidCalculator";
import { As24Helper } from "./As24Helper";

const EMPTY = {
  title: "",
  brand: "",
  model: "",
  price: "",
  year: new Date().getFullYear(),
  km: "",
  transmission: "Automaat",
  fuel: "Benzine",
  body: "SUV",
  ready: false,
  reserved: false,
  in_preparation: false,
  images: [],
  description: "",
  options: [],
  horsepower: "",
  engine_size: "",
  carpass_url: "",
  first_registration: "",
  color_exterior: "",
  color_interior: "",
  chassis: "",
  vat_deductible: false,
  hidden: false,
  is_upcoming: true,
  expected_date: "",
};

const FUELS = ["Benzine", "Diesel", "Elektrisch", "Hybride"];
const BODIES = ["Break", "Cabriolet", "Coupe", "Hatchback", "Sedan", "SUV"];

// Curated brand list (alphabetical) covering luxury + common European brands
const BRANDS = [
  "Alfa Romeo", "Audi", "BMW", "Bentley", "Cadillac", "Chevrolet", "Citroën",
  "Cupra", "DS", "Dodge", "Ferrari", "Fiat", "Ford", "Honda", "Hyundai",
  "Jaguar", "Jeep", "Kia", "Lamborghini", "Land Rover", "Lexus", "Maserati",
  "Mazda", "McLaren", "Mercedes-Benz", "MINI", "Mitsubishi", "Nissan",
  "Opel", "Peugeot", "Polestar", "Porsche", "Range Rover", "Renault",
  "Rolls-Royce", "Seat", "Skoda", "Smart", "Subaru", "Suzuki", "Tesla",
  "Toyota", "Vauxhall", "Volkswagen", "Volvo",
];

// Parse "MM/YYYY" or "YYYY" → year number; fallback to current year
function deriveYear(firstReg) {
  if (!firstReg) return new Date().getFullYear();
  const m = String(firstReg).match(/(\d{4})/);
  return m ? Number(m[1]) : new Date().getFullYear();
}

export default function AdminCarForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, lang } = useI18n();
  const editing = Boolean(id);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optInput, setOptInput] = useState("");
  const [imgInput, setImgInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, current: "" });
  const [dragOver, setDragOver] = useState(false);
  const [existingCars, setExistingCars] = useState([]);
  const dragImg = useRef(null);
  const [bids, setBids] = useState([]);
  const [bidId, setBidId] = useState("");
  const [origBidId, setOrigBidId] = useState("");

  useEffect(() => {
    bidApi.list().then((list) => {
      setBids(list);
      const linked = editing && list.find((b) => b.link_type === "car" && b.link_id === id);
      if (linked) {
        setBidId(linked.id);
        setOrigBidId(linked.id);
      }
    }).catch(() => setBids([]));
  }, [id, editing]);

  const pickBid = (bid) => {
    setBidId(bid);
    const b = bids.find((x) => x.id === bid);
    if (b && (form.price === "" || form.price == null) && b.selling_price) {
      setForm((f) => ({ ...f, price: b.selling_price }));
    }
  };

  const syncBidLink = async (car) => {
    if (bidId === origBidId) return;
    const strip = ({ id: _i, created_at: _c, updated_at: _u, ...rest }) => rest;
    const old = bids.find((b) => b.id === origBidId);
    if (old) await bidApi.update(old.id, { ...strip(old), link_type: null, link_id: null, link_label: null });
    const b = bids.find((x) => x.id === bidId);
    if (b) await bidApi.update(b.id, { ...strip(b), link_type: "car", link_id: car.id, link_label: car.title });
  };

  // Models known for the currently selected brand (from previously created cars)
  const knownModelsForBrand = React.useMemo(() => {
    const b = (form.brand || "").trim().toLowerCase();
    if (!b) return [];
    const set = new Set();
    existingCars.forEach((c) => {
      if ((c.brand || "").trim().toLowerCase() === b && c.model) set.add(c.model);
    });
    return Array.from(set).sort();
  }, [existingCars, form.brand]);

  useEffect(() => {
    carsApi.list().then(setExistingCars).catch(() => setExistingCars([]));
  }, []);

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    carsApi
      .get(id)
      .then((car) => setForm({
        ...EMPTY,
        ...car,
        price: car.price ?? "",
        km: car.km ?? "",
        // Existing cars predating the upcoming feature have NO is_upcoming
        // field at all → spreading would keep EMPTY's default of `true` and
        // accidentally move a saved car back to "Verwacht" on edit.
        is_upcoming: car.is_upcoming === true,
      }))
      .finally(() => setLoading(false));
  }, [id, editing]);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: list.length, current: list[0].name });

    const CHUNK = 5; // upload in parallel batches of 5
    const newUrls = [];
    for (let i = 0; i < list.length; i += CHUNK) {
      const batch = list.slice(i, i + CHUNK);
      const fd = new FormData();
      batch.forEach((f) => fd.append("files", f));
      setUploadProgress({ done: i, total: list.length, current: batch[0].name });
      try {
        const r = await axios.post(`${API_BASE}/upload-multi`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        (r.data.results || []).forEach((item) => {
          if (item.url) newUrls.push(item.url);
        });
      } catch (err) {
        toast({ title: "Upload mislukt", description: `Batch ${i / CHUNK + 1}: ${err?.response?.data?.detail || err.message}` });
      }
    }
    setForm((f) => ({ ...f, images: [...f.images, ...newUrls] }));
    setUploadProgress({ done: list.length, total: list.length, current: "" });
    setUploading(false);
    toast({ title: "Upload klaar", description: `${newUrls.length} foto's toegevoegd.` });
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) uploadFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const moveImage = (from, to) => {
    setForm((f) => {
      const next = [...f.images];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...f, images: next };
    });
  };

  const onDragStartImg = (i) => (dragImg.current = i);
  const onDragOverImg = (e) => e.preventDefault();
  const onDropImg = (i) => {
    if (dragImg.current != null && dragImg.current !== i) moveImage(dragImg.current, i);
    dragImg.current = null;
  };

  const addOption = () => {
    if (!optInput.trim()) return;
    setForm((f) => ({ ...f, options: [...f.options, optInput.trim()] }));
    setOptInput("");
  };

  const removeOption = (idx) => setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));

  const addImageUrl = () => {
    if (!imgInput.trim()) return;
    setForm((f) => ({ ...f, images: [...f.images, imgInput.trim()] }));
    setImgInput("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price === "" || form.price === null ? null : Number(form.price),
        year: deriveYear(form.first_registration),
        km: form.km === "" || form.km === null ? 0 : Number(form.km),
        horsepower: form.horsepower === "" || form.horsepower == null ? null : Number(form.horsepower),
        engine_size: form.engine_size === "" || form.engine_size == null ? null : Number(form.engine_size),
        carpass_url: form.carpass_url?.trim() || null,
        first_registration: form.first_registration?.trim() || null,
        color_exterior: form.color_exterior?.trim() || null,
        color_interior: form.color_interior?.trim() || null,
        chassis: form.chassis?.trim() || null,
        vat_deductible: !!form.vat_deductible,
        hidden: !!form.hidden,
        is_upcoming: !!form.is_upcoming,
        expected_date: form.expected_date?.trim() || null,
      };
      if (editing) {
        await carsApi.update(id, payload);
        await syncBidLink({ id, title: payload.title });
        toast({ title: "Bijgewerkt" });
      } else {
        const created = await carsApi.create(payload);
        await syncBidLink(created);
        toast({ title: "Aangemaakt" });
      }
      navigate("/admin");
    } catch (err) {
      toast({ title: "Opslaan mislukt", description: err?.response?.data?.detail || String(err) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-white/50">{t("admin.loading")}</div>;

  return (
    <div className="p-8 max-w-4xl">
      <Toaster />
      <Link to="/admin" className="inline-flex items-center gap-2 text-white/60 hover:text-[#c5a76f] text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("admin.back")}
      </Link>
      <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
        {editing ? t("admin.editCar") : t("admin.newCar")} {editing && <span className="text-[#c5a76f]">{t("admin.editing")}</span>}
      </h1>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="bg-[#242424] border border-white/5 rounded-md p-6 space-y-5">
          {!editing && (
            <QuickFill
              cars={existingCars}
              onPick={(c) =>
                setForm((f) => ({
                  ...f,
                  brand: c.brand || f.brand,
                  model: c.model || f.model,
                  title: f.title || `${c.brand}${c.model ? ` ${c.model}` : ""}`.trim(),
                }))
              }
            />
          )}
          <Field label={t("admin.title")}>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="inp" data-testid="car-title-input" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("admin.brand")}>
              <input
                required
                list="brand-options"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                className="inp"
                placeholder={t("admin.brandPlaceholder")}
                data-testid="car-brand-input"
              />
              <datalist id="brand-options">
                {BRANDS.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </Field>
            <Field label={t("admin.model")}>
              <input
                list="model-options"
                value={form.model || ""}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="inp"
                placeholder={knownModelsForBrand.length ? t("admin.modelPlaceholder") : t("admin.modelPlaceholderEmpty")}
                data-testid="car-model-input"
              />
              <datalist id="model-options">
                {knownModelsForBrand.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </Field>
            <Field label={t("admin.priceLabel")}>
              <input type="number" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: e.target.value })} className="inp" data-testid="car-price-input" />
            </Field>
            <Field label={t("admin.firstReg")}>
              <input
                required
                type="text"
                value={form.first_registration ?? ""}
                onChange={(e) => setForm({ ...form, first_registration: e.target.value })}
                className="inp"
                placeholder={t("admin.firstRegPh")}
                data-testid="car-first-registration-input"
              />
            </Field>
            <Field label={t("admin.km")}>
              <input
                required
                type="number"
                value={form.km ?? ""}
                onChange={(e) => setForm({ ...form, km: e.target.value })}
                className="inp"
                placeholder={t("admin.kmPh")}
                data-testid="car-km-input"
              />
            </Field>
            <Field label={t("admin.transmission")}>
              <select value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} className="inp">
                <option value="Automaat">{t("admin.transAuto")}</option>
                <option value="Manueel">{t("admin.transManual")}</option>
              </select>
            </Field>
            <Field label={t("admin.fuel")}>
              <select value={form.fuel} onChange={(e) => setForm({ ...form, fuel: e.target.value })} className="inp">
                {FUELS.map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </Field>
            <Field label={t("admin.body")}>
              <select value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="inp">
                {BODIES.map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </select>
            </Field>
            <Field label={t("admin.horsepower")}>
              <input type="number" value={form.horsepower ?? ""} onChange={(e) => setForm({ ...form, horsepower: e.target.value })} className="inp" placeholder={t("admin.horsepowerPh")} />
            </Field>
            <Field label={t("admin.engineSize")}>
              <input type="number" value={form.engine_size ?? ""} onChange={(e) => setForm({ ...form, engine_size: e.target.value })} className="inp" placeholder={t("admin.engineSizePh")} />
            </Field>
          </div>
          <BidLinkSelect bids={bids} carId={id} value={bidId} onChange={pickBid} />
          <Field label={t("admin.carpass")}>
            <input type="url" value={form.carpass_url ?? ""} onChange={(e) => setForm({ ...form, carpass_url: e.target.value })} className="inp" placeholder="https://www.car-pass.be/..." />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={t("admin.colorExt")}>
              <input type="text" value={form.color_exterior ?? ""} onChange={(e) => setForm({ ...form, color_exterior: e.target.value })} className="inp" placeholder={t("admin.colorExtPh")} />
            </Field>
            <Field label={t("admin.colorInt")}>
              <input type="text" value={form.color_interior ?? ""} onChange={(e) => setForm({ ...form, color_interior: e.target.value })} className="inp" placeholder={t("admin.colorIntPh")} />
            </Field>
          </div>
          <Field label="Chassisnummer (VIN)">
            <input
              type="text"
              value={form.chassis ?? ""}
              onChange={(e) => setForm({ ...form, chassis: e.target.value })}
              className="inp"
              placeholder="bv. WBA1234567890XXXX — intern, niet zichtbaar op de website"
              data-testid="car-chassis-input"
            />
            <p className="text-xs text-white/40 mt-1">Enkel voor het garageregister — wordt nooit getoond op de advertentie.</p>
            <VinLookup vin={form.chassis} onApply={(res) => setForm((f) => applyVinResult(f, res, editing))} />
          </Field>
          <label className="flex items-center gap-3 text-white/80 text-sm pt-1">
            <input
              type="checkbox"
              checked={!!form.vat_deductible}
              onChange={(e) => setForm({ ...form, vat_deductible: e.target.checked })}
              className="w-4 h-4 accent-[#c5a76f]"
              data-testid="car-vat-deductible"
            />
            <span className="inline-flex items-center gap-2">
              <span className="px-2 py-0.5 bg-[#c5a76f] text-black text-[10px] font-bold tracking-wider rounded">{t("car.vatDeductible")}</span>
              {t("admin.vatDeductibleDesc")}
            </span>
          </label>
          <div className="space-y-3 pt-1">
            <div className="text-white/60 text-xs uppercase tracking-wider">{t("admin.statusBadge")}</div>
            <label className="flex items-center gap-3 text-white/80 text-sm">
              <input
                type="checkbox"
                checked={form.ready}
                onChange={(e) => setForm({ ...form, ready: e.target.checked, reserved: false })}
                className="w-4 h-4 accent-[#c5a76f]"
                data-testid="car-status-ready"
              />
              <span className="inline-flex items-center gap-2">
                <span className="px-2 py-0.5 bg-[#c5a76f] text-black text-[10px] font-bold tracking-wider rounded">{t("stock.ready")}</span>
                {t("admin.readyDesc")}
              </span>
            </label>
            <label className="flex items-center gap-3 text-white/80 text-sm">
              <input
                type="checkbox"
                checked={form.reserved}
                onChange={(e) => setForm({ ...form, reserved: e.target.checked, ready: false })}
                className="w-4 h-4 accent-[#ef4444]"
                data-testid="car-status-reserved"
              />
              <span className="inline-flex items-center gap-2">
                <span className="px-2 py-0.5 bg-[#ef4444] text-white text-[10px] font-bold tracking-wider rounded">{t("stock.reserved")}</span>
                {t("admin.reservedDesc")}
              </span>
            </label>
            <label className="flex items-center gap-3 text-white/80 text-sm">
              <input
                type="checkbox"
                checked={!!form.is_upcoming}
                onChange={(e) => setForm({ ...form, is_upcoming: e.target.checked })}
                className="w-4 h-4 accent-[#3b82f6]"
                data-testid="car-status-upcoming"
              />
              <span className="inline-flex items-center gap-2">
                <span className="px-2 py-0.5 bg-[#3b82f6] text-white text-[10px] font-bold tracking-wider rounded">{t("stock.upcoming")}</span>
                {t("admin.upcomingDesc")}
              </span>
            </label>
            {form.is_upcoming && (
              <Field label={t("admin.expectedDate")}>
                <input
                  type="text"
                  value={form.expected_date ?? ""}
                  onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
                  className="inp"
                  placeholder={t("admin.expectedDatePh")}
                  data-testid="car-expected-date"
                />
              </Field>
            )}
            <label className="flex items-center gap-3 text-white/80 text-sm pt-2 mt-2 border-t border-white/5">
              <input
                type="checkbox"
                checked={!!form.hidden}
                onChange={(e) => setForm({ ...form, hidden: e.target.checked })}
                className="w-4 h-4 accent-[#f59e0b]"
                data-testid="car-status-hidden"
              />
              <span className="inline-flex items-center gap-2">
                <span className="px-2 py-0.5 bg-[#f59e0b] text-black text-[10px] font-bold tracking-wider rounded">VERBORGEN</span>
                Verberg deze wagen op de website (bv. dubbele AutoScout24-advertentie). Blijft behouden na elke sync.
              </span>
            </label>
          </div>
          <Field label={t("admin.description")}>
            <textarea rows="5" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp" />
          </Field>
        </div>

        <div className="bg-[#242424] border border-white/5 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">
              {t("admin.imagesTitle")}{" "}
              <span className="text-white/40 text-sm font-normal">
                ({form.images.length} {t("admin.photos")})
              </span>
            </h3>
          </div>

          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`block border-2 border-dashed rounded-lg px-6 py-10 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-[#c5a76f] bg-[#c5a76f]/5"
                : "border-white/15 hover:border-[#c5a76f]/50"
            }`}
          >
            <Upload className="w-8 h-8 text-[#c5a76f] mx-auto mb-3" />
            <div className="text-white font-medium">
              {t("admin.dropPhotos")}
            </div>
            <div className="text-white/50 text-xs mt-2">
              {t("admin.dropPhotosHint")}
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInput}
              disabled={uploading}
              className="hidden"
            />
          </label>

          {uploading && (
            <div className="mt-4 px-4 py-3 bg-[#1a1a1a] border border-[#c5a76f]/30 rounded">
              <div className="flex items-center justify-between text-sm text-white mb-2">
                <span>
                  {t("admin.uploading")} <span className="text-white/50">{uploadProgress.current}</span>
                </span>
                <span className="text-[#c5a76f] font-semibold">
                  {uploadProgress.done} / {uploadProgress.total}
                </span>
              </div>
              <div className="h-1.5 bg-white/10 rounded overflow-hidden">
                <div
                  className="h-full bg-[#c5a76f] transition-all"
                  style={{
                    width: `${(uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-5 mb-4">
            <input
              value={imgInput}
              onChange={(e) => setImgInput(e.target.value)}
              placeholder={t("admin.pasteUrl")}
              className="inp flex-1"
            />
            <button
              type="button"
              onClick={addImageUrl}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-sm rounded"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {form.images.length > 0 && (
            <>
              <div className="text-white/50 text-xs mb-3">
                {t("admin.mainPhotoHint")}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {form.images.map((u, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => onDragStartImg(i)}
                    onDragOver={onDragOverImg}
                    onDrop={() => onDropImg(i)}
                    className={`relative group aspect-[4/3] rounded overflow-hidden bg-[#1a1a1a] border-2 ${
                      i === 0 ? "border-[#c5a76f]" : "border-transparent"
                    } cursor-move`}
                  >
                    <img src={imageUrl(u, { thumb: true })} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {i === 0 && (
                      <div className="absolute top-1 left-1 px-2 py-0.5 bg-[#c5a76f] text-black text-[10px] font-bold rounded">
                        {t("admin.mainBadge")}
                      </div>
                    )}
                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="w-6 h-6 rounded-full bg-black/70 text-white/70 flex items-center justify-center">
                        <GripVertical className="w-3.5 h-3.5" />
                      </span>
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="w-6 h-6 rounded-full bg-black/70 text-white hover:bg-red-600 transition-colors flex items-center justify-center"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 text-white/80 text-[10px] rounded">
                      {i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-[#242424] border border-white/5 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">
              {t("admin.optionsTitle")}{" "}
              <span className="text-white/40 text-sm font-normal">
                ({form.options.length} {t("admin.selected")})
              </span>
            </h3>
          </div>

          <div className="space-y-3 mb-5">
            {OPTION_CATEGORIES.map((cat) => (
              <OptionCategoryBlock
                key={cat.label}
                category={cat}
                selected={form.options}
                lang={lang}
                onToggle={(opt) => {
                  setForm((f) => ({
                    ...f,
                    options: f.options.includes(opt)
                      ? f.options.filter((o) => o !== opt)
                      : [...f.options, opt],
                  }));
                }}
              />
            ))}
          </div>

          <div className="pt-4 border-t border-white/5">
            <div className="text-white/60 text-xs uppercase tracking-wider mb-3">
              {t("admin.addCustomOption")}
            </div>
            <div className="flex gap-2">
              <input
                value={optInput}
                onChange={(e) => setOptInput(e.target.value)}
                placeholder={t("admin.customOptionPh")}
                className="inp flex-1"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOption())}
              />
              <button
                type="button"
                onClick={addOption}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-sm rounded"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {form.options.length > 0 && (
            <div className="mt-5 pt-5 border-t border-white/5">
              <div className="text-white/60 text-xs uppercase tracking-wider mb-3">
                {t("admin.selectedOptions")}
              </div>
              <div className="flex flex-wrap gap-2">
                {form.options.map((o) => (
                  <span
                    key={o}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c5a76f]/10 text-[#c5a76f] text-sm rounded"
                  >
                    {translateOption(o, lang)}
                    <button
                      type="button"
                      onClick={() => removeOption(form.options.indexOf(o))}
                      className="text-[#c5a76f]/60 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <As24Helper form={form} />

        <div className="flex items-center gap-3">
          <button disabled={saving} data-testid="car-save-button" className="inline-flex items-center gap-2 px-8 py-3 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase rounded disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? t("admin.saving") : t("admin.save")}
          </button>
          <Link to="/admin" className="px-8 py-3 text-white/60 hover:text-white text-sm tracking-widest uppercase">
            {t("admin.cancel")}
          </Link>
        </div>
      </form>

      <style>{`.inp { width:100%; background:#1a1a1a; border:1px solid rgba(255,255,255,.1); border-radius:6px; padding:10px 14px; color:#fff; font-size:14px; outline:none; transition:border-color .15s }
      .inp:focus { border-color:#c5a76f }`}</style>
    </div>
  );
}

const VIN_FIELD_LABELS = { brand: "merk", model: "model", year: "bouwjaar", horsepower: "pk", engine_size: "cilinderinhoud", fuel: "brandstof", body: "carrosserie", transmission: "transmissie" };

// Fill only empty fields (selects with defaults count as empty for a new car)
function applyVinResult(f, res, editing) {
  const next = { ...f };
  const filled = [];
  const isEmpty = (k) => next[k] === "" || next[k] == null || (!editing && ["year", "fuel", "body", "transmission"].includes(k));
  Object.entries(res.fields || {}).forEach(([k, v]) => {
    if (isEmpty(k) && v != null && v !== "") {
      next[k] = v;
      filled.push(VIN_FIELD_LABELS[k] || k);
    }
  });
  if (!next.title && (next.brand || next.model)) {
    next.title = [next.brand, next.model].filter(Boolean).join(" ");
    filled.push("titel");
  }
  if (!next.description && res.description) {
    next.description = res.description;
    filled.push("omschrijving");
  }
  const newOpts = (res.options || []).filter((o) => !(next.options || []).includes(o));
  if (newOpts.length) {
    next.options = [...(next.options || []), ...newOpts];
    filled.push(`${newOpts.length} opties`);
  }
  res._filled = filled;
  return next;
}

function VinLookup({ vin, onApply }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const valid = /^[A-HJ-NPR-Z0-9]{17}$/i.test((vin || "").trim());

  const run = async () => {
    setBusy(true);
    setInfo(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/vin-lookup`,
        { vin: vin.trim().toUpperCase(), known_options: ALL_OPTIONS },
        { withCredentials: true }
      );
      onApply(data);
      setTimeout(() => setInfo({ ...data, filled: data._filled || [] }), 0);
    } catch (e) {
      toast({ title: "Ophalen mislukt", description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <button type="button" onClick={run} disabled={!valid || busy} data-testid="vin-lookup-button"
        className="inline-flex items-center gap-2 px-3 py-2 border border-[#c5a76f]/40 text-[#c5a76f] hover:bg-[#c5a76f]/10 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs font-semibold uppercase tracking-wider transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />}
        {busy ? "Gegevens ophalen…" : "Ophalen via chassisnummer"}
      </button>
      {!valid && vin && <span className="text-[11px] text-white/35 ml-3">17 tekens nodig</span>}
      {info && (
        <div className="mt-2 text-xs rounded border border-white/10 bg-white/5 px-3 py-2 space-y-1" data-testid="vin-lookup-result">
          <div className="text-white/75">
            {info.filled.length ? <>Ingevuld: <span className="text-[#c5a76f]">{info.filled.join(", ")}</span></> : "Alle velden waren al ingevuld — niets overschreven."}
          </div>
          <div className={info.oem_options_count ? "text-emerald-400/80" : "text-amber-400/80"} data-testid="vin-lookup-oem-status">
            {info.oem_options_count
              ? `${info.oem_options_count} fabrieksopties gevonden`
              : `Fabrieksopties niet beschikbaar: ${info.oem_error || "onbekend"} — de tekst is gemaakt op basis van de technische gegevens.`}
          </div>
          {info.filled.includes("omschrijving") && (
            <div className="text-white/55" data-testid="vin-lookup-translate-note">De Franse en Engelse versie worden automatisch gemaakt zodra je de wagen opslaat.</div>
          )}
          {info.balance && (
            <div className="text-white/35">Tegoed Vincario: {info.balance["API VIN Decode"] ?? "?"} standaard · {info.balance["API OEM VIN Lookup"] ?? "?"} met opties</div>
          )}
        </div>
      )}
    </div>
  );
}

const eurFmt = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n || 0));

function BidLinkSelect({ bids, carId, value, onChange }) {
  const available = bids.filter((b) => !(b.link_type === "car" && b.link_id && b.link_id !== carId));
  return (
    <Field label={<span className="inline-flex items-center gap-1.5"><Calculator className="w-3 h-3" /> Bod uit calculator (optioneel)</span>}>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="inp" data-testid="car-bid-select">
        <option value="">Geen bod koppelen</option>
        {available.map((b) => {
          const r = computeBid(b);
          return (
            <option key={b.id} value={b.id}>
              {(b.number_plate || "Zonder plaat")} · verkoop {eurFmt(b.selling_price)} · bod {eurFmt(r.bodIncl)}
              {b.purchase_price ? ` · aankoop ${eurFmt(b.purchase_price)}` : ""}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[#c5a76f] text-[11px] uppercase tracking-[0.2em] mb-2">{label}</span>
      {children}
    </label>
  );
}

function QuickFill({ cars, onPick }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Build de-duplicated brand+model pairs (most recent first).
  const pairs = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of cars) {
      const key = `${(c.brand || "").toLowerCase()}|${(c.model || "").toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ brand: c.brand, model: c.model });
    }
    return out;
  }, [cars]);

  const suggestions = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return pairs.slice(0, 8);
    return pairs
      .filter(
        (p) =>
          (p.brand || "").toLowerCase().includes(term) ||
          (p.model || "").toLowerCase().includes(term),
      )
      .slice(0, 12);
  }, [q, pairs]);

  return (
    <div ref={ref} className="relative">
      <span className="block text-[#c5a76f] text-[11px] uppercase tracking-[0.2em] mb-2">
        Snelstart — typ merk of model
      </span>
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Bv. Audi, A3, Mustang, Polestar 2 …"
        className="inp"
        data-testid="car-quickfill-input"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-[#1a1a1a] border border-[#c5a76f]/30 rounded shadow-xl">
          {suggestions.map((p, i) => (
            <li key={`${p.brand}-${p.model}-${i}`}>
              <button
                type="button"
                onClick={() => {
                  onPick(p);
                  setQ(`${p.brand}${p.model ? " " + p.model : ""}`);
                  setOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-white/85 hover:bg-[#c5a76f]/10 hover:text-[#c5a76f] transition-colors"
              >
                <span className="text-[#c5a76f]">{p.brand}</span>
                {p.model && <span className="text-white/70"> · {p.model}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-[11px] text-white/40">
        Klik op een voorstel om merk + model + titel vooraf in te vullen, of typ vrij door.
      </p>
    </div>
  );
}

function OptionCategoryBlock({ category, selected, onToggle, lang }) {
  const [open, setOpen] = React.useState(true);
  const count = category.items.filter((i) => selected.includes(i)).length;
  return (
    <div className="border border-white/5 rounded-md overflow-hidden bg-[#1a1a1a]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-white font-medium text-sm">{translateCategory(category.label, lang)}</span>
          {count > 0 && (
            <span className="px-2 py-0.5 bg-[#c5a76f]/15 text-[#c5a76f] text-xs rounded font-semibold">
              {count}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {category.items.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 px-3 py-2 rounded text-sm cursor-pointer transition-colors ${
                  checked
                    ? "bg-[#c5a76f]/15 text-[#c5a76f] border border-[#c5a76f]/30"
                    : "bg-white/[0.02] text-white/75 hover:bg-white/[0.05] border border-transparent"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(opt)}
                  className="sr-only"
                />
                <span
                  className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                    checked ? "bg-[#c5a76f] border-[#c5a76f]" : "border-white/30"
                  }`}
                >
                  {checked && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </span>
                <span className="truncate">{translateOption(opt, lang)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
